import { Line } from '../../../../platform/geom/Line';
import { Matrix } from '../../../../platform/geom/Matrix';
import { Point } from '../../../../platform/geom/Point';
import { Rect } from '../../../../platform/geom/Rect';
import { UniversalSurfaceRenderer } from '../../../../platform/UniversalSurfaceRenderer';
import { resolve } from '../../../Container';
import { AutoLayout } from '../../../packages/autoLayout/AutoLayout';
import { AutoLayoutIndex } from '../../../packages/autoLayout/AutoLayoutIndex';
import { Camera } from '../../Camera';
import { place, swallow, unplace } from '../../commands/OrderCommands';
import { DebugOverlay } from '../../DebugOverlay';
import { EditorSession } from '../../EditorSession';
import { Phys } from '../../entities/Phys';
import { Figure, GuideLine, Physical, RelativeTo, TransformScope } from '../../entities/Physical';
import { Physicals } from '../../entities/Physicals';
import { Root } from '../../entities/Root';
import { GridSystem } from '../../GridSystem';
import { Selection } from '../../Selection';
import { SpatialIndex } from '../../utilities/SpatialIndex';
import { ToolOverlay } from '../ToolOverlay';
import { SelectionTransformation, Snap } from './SelectionTransformation';

export interface SelectionScaleTransformationConfig {
    origin: Point;
    axis: Point;
    handle: Point;
    fixedMode?: boolean;
    forceSnapping?: boolean;
}

export class SelectionScaleTransformation extends SelectionTransformation {

    public readonly type = 'scale';

    @resolve(AutoLayoutIndex) private alIndex: AutoLayoutIndex;
    @resolve(Camera) private camera: Camera;
    @resolve(EditorSession) private session: EditorSession;
    @resolve(GridSystem) private grid: GridSystem;
    @resolve(SpatialIndex) private spatial: SpatialIndex;
    @resolve(ToolOverlay) private overlay: ToolOverlay;

    public axis = Point.zero;
    public angle = 0;

    private baseValues = new Map<Physical, Figure>();
    private childStaticValues = new Map<Physical, Figure>();
    private subjects: Physical[] = [];
    private snapping = false;
    private snaps: Snap[] = [];

    private origin = Point.zero;
    private handle = Point.zero;
    private fixedMode = false;
    private forceSnapping = null;

    public configure(config: SelectionScaleTransformationConfig) {
        this.origin = config.origin;
        this.axis = config.axis;
        this.handle = config.handle;
        this.fixedMode = config.fixedMode;
        this.forceSnapping = config.forceSnapping ?? null;
        return this;
    }

    protected initialize(): void {

        this.burden(
            this.overlay.add(this.paint.bind(this)),
        );

        this.subjects = this.resolveSubjects();
        this.baseValues = Figure.fromManyAsMap(this.subjects);

        if (this.forceSnapping === null) {
            if (this.grid.shouldSnap(this.subjects)) {
                this.snapping = true;
            }
        }
        else {
            this.snapping = this.forceSnapping;
        }

        // TODO: This will need to change if we support a net over multiple objects that is pre-rotated
        if (this.subjects.length === 1) {
            this.angle = this.subjects[0].rotation(RelativeTo.Model);
        }

        // For all children of non-boundary items being scaled, we want them to stay in the same place despite their parents
        // potentially moving, so snapshot their figures, and reapply on each transform tick
        {
            const lookup = new Set<any>(this.subjects);
            const targets = [] as Physical[];
            for (const sb of this.subjects) {
                if (sb.boundary) continue;
                for (const ch of sb.children) {
                    if (lookup.has(ch)) continue;
                    ch.as(Physical, x => targets.push(x));
                }
            }

            this.childStaticValues = Figure.fromManyAsMap(targets);
            // this.dispatch(unplace, [targets]);
        }
    }

    protected apply(): void {
        DebugOverlay.reset();

        const uniformMode = this.latest.modifiers.alt;
        const fixedMode = this.fixedMode || this.latest.modifiers.shift;

        const origin = uniformMode
            ? (new Line(this.origin, this.handle)).mid
            : this.origin;

        // Convert positions into vectors about origin
        let initial = this.handle; // Important: use the handle position not the initial mouse position!
        let latest = this.latest.position;

        initial = initial.subtract(origin).rotate(-this.angle);
        latest = latest.subtract(origin).rotate(-this.angle);

        // If required, project onto the axis of scale
        if (fixedMode) {
            initial = initial.project(this.axis);
            latest = latest.project(this.axis);
        }

        // Hack:
        // We have issues with snapping fixedMode corner scales, so just disable for now:
        const isCorner = this.axis.x !== 0 && this.axis.y !== 0;

        if (this.snapping && !(fixedMode && isCorner)) {

            if (this.grid.gridSnap) {
                latest = this.grid.snap(latest);
            }

            if (this.grid.guideSnap) {
                const transformSoFar = Matrix.identity
                    .translate(origin)
                    .rotate(this.angle)
                    .scale(latest.divide(initial).fixNaN())
                    .rotate(-this.angle)
                    .translate(origin.multiply(-1));

                const bounds = Rect.fromPoints(
                    Array.from(this.baseValues.values()).flatMap(x => transformSoFar.apply(x))
                );

                // Work out what guide we care about
                const guides = this.computeApplicableGuidesFor(bounds);
                this.snaps = this.querySnapsFor(guides);
                const snapX = this.snaps.find(s => s.guide.axis === 'x');
                const snapY = this.snaps.find(s => s.guide.axis === 'y');

                latest = latest
                    .add(snapX?.vector ?? Point.zero)
                    .add(snapY?.vector ?? Point.zero);
            }
        }

        // Generate the matrix, we must unrotate, scale then re-rotate in order to make scaling work properly.  If you want to know
        // why we do this, comment it out and try it... ;-D

        const scale = latest.divide(initial).fixNaN();
        const transform = Matrix.identity
            .translate(origin)
            .rotate(this.angle)
            .scale(scale)
            .rotate(-this.angle)
            .translate(origin.multiply(-1));

        this.update(() => {
            this.applyTransform(transform);
            // For some reason dont need to rebase
            // this.fixHangingSubjects(this.subjects);
            this.emit('apply', [this.latest]);
        });
    }

    protected commit(): void {
        this.update(u => {
            const ids = this.subjects.map(x => x.id);
            // We want to place the subjects
            this.fixHangingSubjects();
            this.fixHangingChildren();
            this.emit('commit', [ids]);
            this.dispatch(place, [ids]);
            this.dispatch(swallow, [ids]);
            // // But also any elements they are covering (either side)
            // const covers = new Set<Physical>();
            // const lookup = new Set(this.subjects);

            // for (const sb of this.subjects) {
            //     const sbb = sb.bounds(RelativeTo.Model);
            //     for (const cch of this.spatial.lookup(sb.bounds(RelativeTo.Model))) {
            //         // If in subjects ignore
            //         if (lookup.has(cch)) continue;
            //         // If not "above" ignore
            //         if (!Physicals.isAbove(cch, sb)) continue;
            //         // If not contained fully by subject ignore
            //         if (!sbb.contains(cch.bounds(RelativeTo.Model))) continue;
            //         // Otherwise candidate
            //         covers.add(cch);
            //     }
            // }

            // this.dispatch(place, [Physicals.top(covers, true).map(x => x.id)]);

            // Use noop just to be sure changes were made
            u.noop();
        });
    }

    private applyTransform(transform: Matrix) {
        const elmts = Array.from(this.baseValues.keys());
        const scope = new TransformScope(elmts, 'scale');
        for (const e of elmts) {
            const from = this.baseValues.get(e);
            const to = transform.apply(from);
            e.applyTransformation(scope, from, to);
        }
        // Apply child resets
        for (const [ch, fg] of this.childStaticValues.entries()) {
            ch.setFromFigure(fg);
        }
    }

    private resolveSubjects() {
        const selected = this.session.get(Selection).polarize(Physical);
        const expanded = selected.flatMap(x => x.is(AutoLayout) ? [x, ...this.alIndex.findElements(x.id)] : [x]);
        return Phys.from(expanded).orderBackToFront().toArray();
    }

    private fixHangingSubjects() {

        const shouldUnplace = !!this.subjects.find(s => {
            if (s.parent.is(Root)) return false;
            const spo = s.parentPhysical.outline(RelativeTo.Model);
            const so = s.outline(RelativeTo.Model);
            if (spo.contains(so)) return false;
            return true;
        });

        if (shouldUnplace) {
            this.dispatch(unplace, [this.subjects.map(x => x.id)]);
            return true;
        }

        return false;
    }

    private fixHangingChildren() {

        const children = this.subjects.flatMap(x =>
            !x.boundary ? x.polarize(Physical) : []
        );

        const shouldUnplace = !!children.find(s => {
            if (s.parent.is(Root)) return false;
            const spo = s.parentPhysical.outline(RelativeTo.Model);
            const so = s.outline(RelativeTo.Model);
            if (spo.contains(so)) return false;
            return true;
        });

        if (shouldUnplace) {
            this.dispatch(unplace, [children.map(x => x.id)]);
            return true;
        }

        return false;
    }

    protected findSnappingCandidatesFor(guides: GuideLine[]) {

        const ignore = new Set(this.subjects);
        const view = this.camera.getModelFrame();
        const lines = guides.map(g => (
            (g.axis === 'x')
                ? Line.from([view.left, g.position, view.right, g.position])
                : Line.from([g.position, view.top, g.position, view.bottom])
        ));

        const extrusions = lines.map(x => Rect.fromPoints(x.extrude(10, 10)));
        const results = new Set<Physical>();

        for (const e of extrusions) {
            const elmts = this.spatial.lookup(e);
            for (const p of elmts) {
                if (ignore.has(p)) continue;
                results.add(p);
            }
        }

        return results;
    }

    private computeApplicableGuidesFor(bounds: Rect) {

        // Work out what guide we care about
        const ray = Line.ray(this.origin, Point.vector(this.origin, this.handle));
        const edges = bounds.lines();

        const hits = Rect.fromPoints(Array.from(this.baseValues.values()).flat())
            .scale(1.1)
            .lines()
            .map((x, i) => [x.extend(10), i] as const)
            .filter(([x]) => x.intersects(ray))
            .map(([_x, i]) => edges[i]);

        return hits
            .map(x => GuideLine.fromLine(x))
            .filter(x => !!x);
    }

    private paint(gfx: UniversalSurfaceRenderer) {
        const mvm = this.camera.modelViewMatrix;
        const subjectBounds = Physicals.bounds(this.subjects);

        this.paintSnaps(gfx, mvm, subjectBounds, this.snaps);
    }
}
